import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  ColumnDef,
  SortingState,
  createAngularTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/angular-table';
import { HlmInput } from '@spartan-ng/helm/input';
import { HlmNumberedPagination } from '@spartan-ng/helm/pagination';
import { HlmSpinner } from '@spartan-ng/helm/spinner';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { LucideArrowDown, LucideArrowUp, LucideChevronsUpDown, LucideSearch } from '@lucide/angular';
import { DataTableAlign, DataTableCellContext, DataTableColumn } from './data-table.types';

/**
 * Tabla de datos genérica y reutilizable construida sobre TanStack Table
 * (`@tanstack/angular-table`) y los estilos de SpartanUI (`hlmTable`).
 *
 * Se configura de forma declarativa pasando `data` y `columns`; soporta
 * ordenamiento, paginación opcional, estado de carga, estado vacío, celdas
 * personalizadas por plantilla y selección/click de fila.
 *
 * @template T Tipo de cada fila de datos.
 */
@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    HlmTableImports,
    HlmInput,
    HlmSpinner,
    HlmNumberedPagination,
    LucideArrowUp,
    LucideArrowDown,
    LucideChevronsUpDown,
    LucideSearch,
  ],
  templateUrl: './data-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent<T> {
  /** Filas a mostrar. */
  readonly data = input<T[]>([]);

  /** Definición de columnas (encabezado, accessor, alineación, orden, celda, etc.). */
  readonly columns = input<DataTableColumn<T>[]>([]);

  /** Muestra el spinner de carga en lugar de las filas. */
  readonly loading = input(false);

  /** Texto mostrado mientras `loading` es `true`. */
  readonly loadingMessage = input('Cargando...');

  /** Texto mostrado cuando no hay filas. */
  readonly emptyMessage = input('No hay registros.');

  /** Si es `true`, muestra un campo de búsqueda que filtra las filas. */
  readonly filterable = input(false);

  /** Placeholder del campo de búsqueda. */
  readonly filterPlaceholder = input('Buscar...');

  /** Cantidad de filas por página inicial. Si es `null` o `0`, se desactiva la paginación. */
  readonly pageSize = input<number | null>(null);

  /** Opciones de tamaño de página ofrecidas en el selector del paginador. */
  readonly pageSizes = input<number[]>([5, 10, 20, 50]);

  /** Si es `true`, muestra el selector de tamaño de página en el paginador. */
  readonly showPageSizeSelector = input(true);

  /**
   * Altura mínima del área de la tabla (p. ej. `'26rem'`). Reserva espacio para que
   * el contenedor no se encoja al cargar o cuando hay menos filas que una página completa.
   */
  readonly minHeight = input<string | null>(null);

  /** Si es `true`, las filas son clicables y emiten `rowClick`. */
  readonly rowClickable = input(false);

  /** Función que devuelve el identificador de una fila (para resaltar la seleccionada). */
  readonly trackBy = input<((row: T) => unknown) | null>(null);

  /** Identificador de la fila actualmente seleccionada (se resalta). */
  readonly selectedId = input<unknown>(null);

  /** Se emite al hacer click en una fila (sólo si `rowClickable` es `true`). */
  readonly rowClick = output<T>();

  // Estado controlado de TanStack
  private readonly sorting = signal<SortingState>([]);
  private readonly pageIndex = signal(0);
  protected readonly globalFilter = signal('');

  // Tamaño de página efectivo (se inicializa desde `pageSize` y el usuario puede cambiarlo)
  protected readonly pageSizeState = linkedSignal(() => this.pageSize() || 10);

  // Página actual en base 1, para el paginador de SpartanUI
  protected readonly currentPage = computed(() => this.pageIndex() + 1);

  // Columnas en formato TanStack (sólo para el ordenamiento; el render es propio)
  private readonly tableColumns = computed<ColumnDef<T>[]>(() =>
    this.columns().map((c) => ({
      id: c.key,
      accessorFn: (row: T) => (c.accessor ? c.accessor(row) : ''),
      enableSorting: !!c.sortable,
    }))
  );

  /** Instancia reactiva de la tabla de TanStack. */
  readonly table = createAngularTable<T>(() => ({
    data: this.data(),
    columns: this.tableColumns(),
    state: {
      sorting: this.sorting(),
      globalFilter: this.globalFilter(),
      pagination: { pageIndex: this.pageIndex(), pageSize: this.effectivePageSize() },
    },
    onSortingChange: (updater) =>
      this.sorting.set(typeof updater === 'function' ? updater(this.sorting()) : updater),
    onGlobalFilterChange: (updater) => {
      const next = typeof updater === 'function' ? updater(this.globalFilter()) : updater;
      this.globalFilter.set(next ?? '');
      this.pageIndex.set(0);
    },
    onPaginationChange: (updater) => {
      const current = { pageIndex: this.pageIndex(), pageSize: this.effectivePageSize() };
      const next = typeof updater === 'function' ? updater(current) : updater;
      this.pageIndex.set(next.pageIndex);
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  }));

  /** Tamaño de página efectivo: el del selector, o todas las filas si la paginación está desactivada. */
  private effectivePageSize(): number {
    return this.showPagination() ? this.pageSizeState() : Math.max(this.data().length, 1);
  }

  /** Indica si deben mostrarse los controles de paginación. */
  protected showPagination(): boolean {
    const ps = this.pageSize();
    return !!ps && ps > 0;
  }

  /** Cambia la página actual (el paginador trabaja en base 1; TanStack en base 0). */
  protected onCurrentPageChange(page: number): void {
    this.pageIndex.set(Math.max(0, page - 1));
  }

  /** Cambia el tamaño de página y vuelve a la primera página. */
  protected onItemsPerPageChange(size: number): void {
    this.pageSizeState.set(size);
    this.pageIndex.set(0);
  }

  /** Actualiza el filtro de búsqueda y reinicia a la primera página. */
  protected setFilter(value: string): void {
    this.globalFilter.set(value ?? '');
    this.pageIndex.set(0);
  }

  /** Cantidad de filas tras aplicar el filtro. */
  protected filteredCount(): number {
    return this.table.getFilteredRowModel().rows.length;
  }

  /** Alterna el ordenamiento de una columna. */
  protected toggleSort(key: string): void {
    this.table.getColumn(key)?.toggleSorting();
  }

  /** Dirección de ordenamiento actual de una columna (`'asc'`, `'desc'` o `false`). */
  protected sortDirection(key: string): 'asc' | 'desc' | false {
    return this.table.getColumn(key)?.getIsSorted() ?? false;
  }

  /** Indica si una fila está seleccionada según `trackBy` y `selectedId`. */
  protected isSelected(row: T): boolean {
    const track = this.trackBy();
    if (!track || this.selectedId() == null) {
      return false;
    }
    return track(row) === this.selectedId();
  }

  /** Emite el click de fila si las filas son clicables. */
  protected onRowClick(row: T): void {
    if (this.rowClickable()) {
      this.rowClick.emit(row);
    }
  }

  /** Valor textual por defecto de una celda. */
  protected displayValue(col: DataTableColumn<T>, row: T): string {
    const value = col.accessor?.(row);
    return value == null ? '' : String(value);
  }

  /** Contexto pasado a la plantilla personalizada de una celda. */
  protected cellContext(col: DataTableColumn<T>, row: T): DataTableCellContext<T> {
    return { $implicit: row, row, value: col.accessor?.(row) };
  }

  /** Clases del encabezado de una columna (alineación + clases extra). */
  protected headClasses(col: DataTableColumn<T>): string {
    return [this.alignClass(col.align), col.headClass ?? ''].filter(Boolean).join(' ');
  }

  /** Clases de la celda de una columna (alineación + clases extra). */
  protected cellClasses(col: DataTableColumn<T>): string {
    return [this.alignClass(col.align), col.cellClass ?? ''].filter(Boolean).join(' ');
  }

  private alignClass(align?: DataTableAlign): string {
    switch (align) {
      case 'right':
        return 'text-right';
      case 'center':
        return 'text-center';
      default:
        return 'text-left';
    }
  }
}
